import Cookies from 'js-cookie';
import md5 from 'md5';
import APP_CONFIG from '@/appConfig';

// Secret key for cookie signing (Note: Move to server-side configuration for production)
const MEMBER_GATE_SECRET = 'kJ8$mN2@pL9#vQ5&xR7*wE3!zT6%uY4^sF1+aC0-bD8~gH5@mK2#';

/**
 * Cookie utility functions for secure cookie management
 */
export default class CookieUtils {
  /**
   * Set the member gate verified cookie with optional hardening
   * @param {Object} options - Options for cookie creation
   * @param {string} options.userId - User ID for validation (optional)
   * @param {boolean} options.useHardening - Whether to use hardening (default: false)
   */
  static setMemberGateVerified(options = {}) {
    const { userId = '', useHardening = false } = options;

    if (useHardening) {
      // Hardened approach: Create a signed cookie with timestamp and user data
      const timestamp = Date.now();
      const value = '1';
      const payload = `${value}|${timestamp}|${userId}`;

      // Create a hash signature (Note: For production, this should use a server-side secret)
      const signature = md5(`${payload}|${APP_CONFIG.site.domain}|${MEMBER_GATE_SECRET}`);
      const signedValue = `${payload}|${signature}`;

      Cookies.set('member_gate_verified', signedValue, {
        expires: 365,
        domain: APP_CONFIG.site.domain,
        secure: window.location.protocol === 'https:',
        sameSite: 'strict',
      });
    } else {
      // Simple approach: Just set the basic cookie
      Cookies.set('member_gate_verified', '1', {
        expires: 365,
        domain: APP_CONFIG.site.domain,
      });
    }
  }

  /**
   * Validate the member gate verified cookie
   * @param {Object} options - Options for validation
   * @param {string} options.userId - User ID for validation (optional)
   * @param {number} options.maxAge - Maximum age in milliseconds (default: 365 days)
   * @returns {boolean} - Whether the cookie is valid
   */
  static validateMemberGateVerified(options = {}) {
    const { userId = '', maxAge = 365 * 24 * 60 * 60 * 1000 } = options;
    const cookieValue = Cookies.get('member_gate_verified');

    if (!cookieValue) {
      return false;
    }

    // Check if it's a simple cookie (just '1')
    if (cookieValue === '1') {
      return true;
    }

    // Check if it's a hardened cookie (contains pipes)
    if (cookieValue.includes('|')) {
      try {
        const parts = cookieValue.split('|');
        if (parts.length !== 4) {
          return false;
        }

        const [value, timestamp, cookieUserId, signature] = parts;

        // Validate timestamp (not too old)
        const cookieAge = Date.now() - parseInt(timestamp, 10);
        if (cookieAge > maxAge) {
          return false;
        }

        // Validate user ID if provided
        if (userId && cookieUserId !== userId) {
          return false;
        }

        // Validate signature
        const expectedPayload = `${value}|${timestamp}|${cookieUserId}`;
        const expectedSignature = md5(`${expectedPayload}|${APP_CONFIG.site.domain}|${MEMBER_GATE_SECRET}`);

        return signature === expectedSignature;
      } catch (error) {
        console.warn('Invalid member gate cookie format:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Remove the member gate verified cookie
   */
  static removeMemberGateVerified() {
    Cookies.remove('member_gate_verified', { domain: APP_CONFIG.site.domain });
  }
}

/*
HARDENING SUGGESTIONS FOR PRODUCTION:

1. **Server-Side Secret**:
   - Move the secret key to server-side configuration
   - Have the server generate and validate the signature
   - API endpoint: POST /api/auth/set-member-gate-cookie

2. **JWT-like Approach**:
   - Use a proper JWT library to create signed tokens
   - Include user ID, timestamp, and permissions in the JWT payload
   - Validate JWT signature server-side

3. **Cryptographic Improvements**:
   - Use HMAC-SHA256 instead of MD5 for better security
   - Add salt/nonce to prevent rainbow table attacks
   - Rotate secrets periodically

4. **Additional Security Measures**:
   - Set HttpOnly flag (requires server-side setting)
   - Implement rate limiting on cookie validation
   - Log suspicious cookie manipulation attempts
   - Use short expiry times with automatic renewal

5. **Server-Side Validation**:
   - Store valid cookie hashes in database/redis
   - Validate against server records on each request
   - Invalidate cookies on logout or suspicious activity

Example server-side validation endpoint:
```javascript
// Backend validation
app.post('/api/auth/validate-member-gate', (req, res) => {
  const { cookieValue } = req.body;
  const isValid = validateCookieServerSide(cookieValue, req.user.id);
  res.json({ valid: isValid });
});
```

6. **Recommended Implementation**:
   - Use hardening for sensitive operations
   - Implement server-side validation endpoint
   - Add client-side validation as additional layer
   - Monitor for tampering attempts in analytics
*/
